/**
 * Where the app sends the user when the session is lost mid-use (a backend
 * 401, or silent token renewal giving up).
 *
 * Deliberately the app's own /login page rather than Auth0 directly. Auth0
 * ties each /authorize redirect to a short-lived transaction; opening its
 * page on the app's initiative — from a background request, in a tab the
 * user is not looking at — leaves that transaction to expire or be displaced
 * by another tab's redirect, and the identifier form then fails with
 * "invalid_request: we couldn't find your session". Landing in-app means the
 * Auth0 transaction is created by the user's click, seconds before use.
 */
export const SESSION_ENDED_REASON = 'session-ended';

export function sessionEndedLoginPath(
    pathname: string,
    search: string
): string {
    return loginPath(pathname, search, SESSION_ENDED_REASON);
}

/**
 * The same landing for someone with no session at all opening a protected
 * URL (a deep link, a bookmark, the post-logout return). Sending them
 * straight to Auth0 from a page load had the same weakness as the mid-use
 * case: the transaction was minted on load, not on their click, so a login
 * form left open went stale. No reason parameter — this is a plain sign-in.
 */
export function unauthenticatedLoginPath(
    pathname: string,
    search: string
): string {
    return loginPath(pathname, search, null);
}

function loginPath(
    pathname: string,
    search: string,
    reason: string | null
): string {
    const params = new URLSearchParams(reason ? { reason } : {});
    const returnTo = `${pathname}${search}`;
    // The login page itself, and the bare home page, are not worth
    // returning to — home is where a plain login lands anyway.
    if (returnTo && !returnTo.startsWith('/login') && returnTo !== '/') {
        params.set('returnTo', returnTo);
    }
    const query = params.toString();
    return query ? `/login?${query}` : '/login';
}

export function parseLoginParams(search: string): {
    sessionEnded: boolean;
    returnTo: string | undefined;
} {
    const params = new URLSearchParams(search);
    const returnTo = params.get('returnTo') ?? undefined;
    return {
        sessionEnded: params.get('reason') === SESSION_ENDED_REASON,
        // Only same-app paths: a full URL here would be an open redirect.
        returnTo:
            returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
                ? returnTo
                : undefined,
    };
}
